/**
 * SQL 품질 검사 — 검사 기준(메모 1) 검사 유형/규칙). 기계 검사기(가·나·마)의 켜기·심각도·값 + 「SQL 표준」 가이드에서 고른 규칙(AI 판정) + 업무구분 매핑.
 * 바뀐 것의 이력은 서버가 앞 판과 견주어 쓴다(메모 4)-다) — 화면은 보여 주기만 한다.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiPlus, FiBookOpen, FiX, FiRefreshCw, FiCopy } from 'react-icons/fi';
import { useConfirm } from '../ConfirmDialog';
import { formatApiError } from '../../utils/formatters';
import WorkflowHead, { WorkflowRibbon } from '../common/WorkflowHead';
import { storedTime } from '../../utils/storedTime';
import { CHECK_GROUPS } from '../../utils/sqlQualityMarks';
import * as api from '../../services/sqlQuality';

const emptyBody = (defaults) => ({ checks: JSON.parse(JSON.stringify(defaults || {})), guideRules: [], biz: [], patterns: [] });

/** 이력 한 줄 — 서버가 남긴 바뀐 것 */
export function describeChange(c, t) {
  if (c.type === 'created') return t('common:sqq.hist.created', { checks: c.checks, rules: c.rules, patterns: c.patterns || 0 });
  if (c.type === 'name') return t('common:sqq.hist.name', { from: c.from, to: c.to });
  if (c.type === 'biz') return t('common:sqq.hist.biz', { count: c.count });
  if (c.type === 'rule') return t(c.op === 'add' ? 'common:sqq.hist.ruleAdd' : 'common:sqq.hist.ruleRemove', { title: c.title });
  if (c.type === 'pattern') return t(`common:sqq.hist.pattern.${c.op}`, { name: c.name });
  if (c.type === 'check') {
    const name = t(`common:sqq.check.${c.id}`);
    if (c.field === 'on') return t(c.to ? 'common:sqq.hist.checkOn' : 'common:sqq.hist.checkOff', { name });
    if (c.field === 'severity') return t('common:sqq.hist.severity', { name, from: t(`common:sqq.sev.${c.from}`), to: t(`common:sqq.sev.${c.to}`) });
    return t('common:sqq.hist.param', { name, key: t(`common:sqq.param.${c.key}`), from: String(c.from), to: String(c.to) });
  }
  return c.type;
}

function ParamInputs({ id, params, onChange, disabled }) {
  const { t } = useTranslation();
  const set = (k, v) => onChange({ ...params, [k]: v });
  return (
    <span className="sqq-params">
      {Object.entries(params).map(([k, v]) => {
        if (typeof v === 'boolean') return <label key={k} className="stn-row"><input type="checkbox" checked={v} onChange={e => set(k, e.target.checked)} disabled={disabled} /> {t(`common:sqq.param.${k}`)}</label>;
        if (k === 'case') return <label key={k} className="stn-row">{t('common:sqq.param.case')} <select value={v} onChange={e => set(k, e.target.value)} disabled={disabled}><option value="lower">{t('common:sqq.caseLower')}</option><option value="upper">{t('common:sqq.caseUpper')}</option></select></label>;
        return <label key={k} className="stn-row">{t(`common:sqq.param.${k}`)} <input type="number" min="0" step="any" className="sqq-num" value={v} onChange={e => set(k, Number(e.target.value))} disabled={disabled} /></label>;
      })}
      {id === 'perf' && <span className="stn-note">{t('common:sqq.perfHint')}</span>}
    </span>
  );
}
ParamInputs.propTypes = { id: PropTypes.string.isRequired, params: PropTypes.object.isRequired, onChange: PropTypes.func.isRequired, disabled: PropTypes.bool };

/** 기준 만들기·고치기 단계 — SQL 튜닝·작성과 같은 리본(번호 단계 + 이력) */
export const CRIT_STEPS = ['basic', 'checks', 'rules', 'patterns', 'biz', 'review'];
const CRIT_TABS = [...CRIT_STEPS, 'history'];

/** 기준 요약 — ① 기본 · ⑥ 확인 · 검사의 「기준」 단계가 함께 쓴다 */
export function CriteriaSummary({ body, t }) {
  const checks = Object.entries((body && body.checks) || {});
  const on = checks.filter(([, v]) => v.on);
  return (
    <div className="sqq-kpis">
      {[['checks', `${on.length} / ${checks.length}`], ['rules', ((body && body.guideRules) || []).length], ['patterns', ((body && body.patterns) || []).length], ['biz', ((body && body.biz) || []).length]].map(([k, v]) => (
        <div key={k} className="sqq-kpi"><span className="stn-note">{t(`common:sqq.critKpi.${k}`)}</span><b>{v}</b></div>
      ))}
    </div>
  );
}
CriteriaSummary.propTypes = { body: PropTypes.object, t: PropTypes.func.isRequired };

/**
 * 고른 기준 하나를 단계로 보이고 고친다. 목록은 창의 왼쪽(「검사 기준」)에 있다.
 * @param selected { id } 열 기준 · { id: null, copyOf? } 새 기준(복사면 그 내용으로 시작) · null 아무것도
 * 기본·샘플 기준(builtin)은 읽기 전용 — 「복사해서 내 기준 만들기」 만 된다.
 */
const CriteriaEditor = ({ selected, onSaved, onDeleted, onCopy, onOpenGuides }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [defaults, setDefaults] = useState(null);
  const [guides, setGuides] = useState([]);
  const [sel, setSel] = useState(null);                // 서버 판(새 것이면 { id: null, history: [] })
  const [form, setForm] = useState(null);              // { name, body }
  const [step, setStep] = useState('basic');
  const [busy, setBusy] = useState(false);
  const tRef = useRef(t);
  tRef.current = t;

  const loadRefs = useCallback(async () => {
    try {
      const [k, g] = await Promise.all([api.getChecks(), api.getStandardRules()]);
      setDefaults(k.defaults); setGuides(g.guides || []);
    } catch (err) { toast.error(formatApiError(err, tRef.current)); }
  }, []);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  const selKey = selected ? (selected.id || `new:${selected.copyOf ? selected.copyOf.id : ''}:${selected.nonce || ''}`) : null;
  useEffect(() => {
    if (!selected) { setSel(null); setForm(null); return; }
    setStep('basic');
    if (!selected.id) {
      const src = selected.copyOf;
      const body = src ? JSON.parse(JSON.stringify(src.body)) : emptyBody(defaults);
      setSel({ id: null, history: [] });
      setForm({ name: src ? t('common:sqq.copyName', { name: src.name }) : '', body });
      return;
    }
    let alive = true;
    api.getCriteria(selected.id).then((r) => { if (!alive) return; setSel(r.criteria); setForm({ name: r.criteria.name, body: r.criteria.body }); })
      .catch(err => toast.error(formatApiError(err, tRef.current)));
    return () => { alive = false; };   // eslint-disable-line consistent-return
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 고른 기준이 바뀔 때만(기본값은 새 기준을 만들 때 한 번 읽는다)
  }, [selKey, defaults === null]);

  const readOnly = !!(sel && sel.builtin);
  const dirty = !!(form && sel && (sel.id === null || form.name !== sel.name || JSON.stringify(form.body) !== JSON.stringify(sel.body)));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.saveCriteria({ id: sel.id, name: form.name, body: form.body });
      setSel(r.criteria); setForm({ name: r.criteria.name, body: r.criteria.body });
      toast.success(t('common:sqq.saved'), { autoClose: 1200 });
      if (onSaved) onSaved(r.criteria);
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(false); }
  };
  const remove = async () => {
    const ok = await confirm({ title: t('common:sqq.critDeleteTitle'), message: t('common:sqq.critDeleteBody', { name: sel.name }), confirmText: t('common:ui.delete'), variant: 'danger' });
    if (!ok) return;
    try { await api.deleteCriteria(sel.id); if (onDeleted) onDeleted(sel.id); } catch (err) { toast.error(formatApiError(err, t)); }
  };

  if (!selected) return <div className="stn-empty-main">{t('common:sqq.critIntro')}</div>;
  if (!form || !sel) return <div className="stn-note">{t('common:sqq.loading')}</div>;
  const body = form.body;
  const setCheck = (id, patch) => setForm({ ...form, body: { ...body, checks: { ...body.checks, [id]: { ...body.checks[id], ...patch } } } });
  const picked = new Set((body.guideRules || []).map(r => r.ruleId));
  const toggleRule = (g, r) => {
    const cur = body.guideRules || [];
    const next = picked.has(r.id) ? cur.filter(x => x.ruleId !== r.id) : [...cur, { guideId: g.id, ruleId: r.id, principle: r.principle }];
    setForm({ ...form, body: { ...body, guideRules: next } });
  };
  const setBiz = (biz) => setForm({ ...form, body: { ...body, biz } });
  const setPatterns = (patterns) => setForm({ ...form, body: { ...body, patterns } });
  const idx = CRIT_STEPS.indexOf(step);

  const renderBasic = () => (
    <>
      <label className="stn-field"><span>{t('common:sqq.critName')}</span>
        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t('common:sqq.critNamePh')} /></label>
      {sel.description && <div className="sqq-desc">{sel.description}</div>}
      <CriteriaSummary body={body} t={t} />
    </>
  );

  const renderChecks = () => CHECK_GROUPS.map(gr => (
    <div key={gr.id} className="stn-box">
      <b>{t(`common:sqq.group.${gr.id}`)}</b>
      <span className="stn-note">{t(`common:sqq.groupHint.${gr.id}`)}</span>
      {gr.checks.map((id) => {
        const c = body.checks[id];
        if (!c) return null;
        return (
          <div key={id} className={`sqq-check${c.on ? '' : ' off'}`}>
            <label className="stn-row sqq-check-name"><input type="checkbox" checked={c.on} onChange={e => setCheck(id, { on: e.target.checked })} /> <b>{t(`common:sqq.check.${id}`)}</b></label>
            <span className="stn-note sqq-check-desc">{t(`common:sqq.checkDesc.${id}`)}</span>
            <span className="stn-row">
              <select value={c.severity} onChange={e => setCheck(id, { severity: e.target.value })} disabled={!c.on} aria-label={t('common:sqq.severity')}>
                {['high', 'mid', 'low'].map(s => <option key={s} value={s}>{t(`common:sqq.sev.${s}`)}</option>)}
              </select>
              <ParamInputs id={id} params={c.params} onChange={params => setCheck(id, { params })} disabled={!c.on} />
            </span>
          </div>
        );
      })}
    </div>
  ));

  const renderRules = () => (
    <div className="stn-box">
      <div className="stn-row stn-between">
        <b>{t('common:sqq.rulesTitle')} ({picked.size})</b>
        <span className="stn-row">
          {/* 가이드 창에서 만들거나 배포한 뒤 돌아왔을 때 — 목록은 이 화면을 열 때 한 번 읽는다 */}
          <button type="button" className="app-btn" onClick={loadRefs} title={t('common:sqq.reloadRules')} aria-label={t('common:sqq.reloadRules')}><FiRefreshCw size={12} /></button>
          {onOpenGuides && <button type="button" className="app-btn" onClick={onOpenGuides}><FiBookOpen size={12} /> {t('common:sqq.manageGuides')}</button>}
        </span>
      </div>
      <span className="stn-note">{t('common:sqq.rulesHint')}</span>
      {!guides.length && <span className="stn-note">{t('common:sqq.noStandardGuide')}</span>}
      {guides.map(g => (
        <div key={g.id} className="sqq-guide">
          <div className="stn-row"><b>{g.title}</b> <span className="stn-note">{t('common:sqq.ruleCount', { picked: g.rules.filter(r => picked.has(r.id)).length, total: g.rules.length })}</span>
            {g.status !== 'published' && <span className="stn-warn">{t('common:sqq.guideDraft')}</span>}</div>
          {g.rules.map(r => (
            <label key={r.id} className={`sqq-rule${g.status !== 'published' ? ' off' : ''}`}>
              <input type="checkbox" checked={picked.has(r.id)} disabled={g.status !== 'published'} onChange={() => toggleRule(g, r)} />
              <span>{r.topic ? <span className="stn-note">{r.topic} · </span> : null}{r.when ? <span className="stn-note">[{r.when}] </span> : null}{r.principle}</span>
            </label>
          ))}
        </div>
      ))}
    </div>
  );

  const renderPatterns = () => (
    <div className="stn-box">
      <span className="stn-note">{t('common:sqq.patternHint')}</span>
      {(body.patterns || []).map((p, i) => {
        const setP = (patch) => setPatterns(body.patterns.map((x, j) => (j === i ? { ...x, ...patch } : x)));
        return (
          // eslint-disable-next-line react/no-array-index-key -- 아직 id 가 없는 새 패턴이 있다(저장 때 서버가 붙인다)
          <div key={p.id || `new-${i}`} className={`sqq-pattern${p.on === false ? ' off' : ''}`}>
            <div className="stn-row">
              <input type="checkbox" checked={p.on !== false} onChange={e => setP({ on: e.target.checked })} aria-label={t('common:sqq.patternOn')} />
              <input className="stn-grow" value={p.name || ''} placeholder={t('common:sqq.patternNamePh')} onChange={e => setP({ name: e.target.value })} />
              <select value={p.mode || 'keywords'} onChange={e => setP({ mode: e.target.value })} aria-label={t('common:sqq.patternMode')}>
                <option value="keywords">{t('common:sqq.patternModes.keywords')}</option>
                <option value="sample">{t('common:sqq.patternModes.sample')}</option>
              </select>
              <select value={p.severity || 'mid'} onChange={e => setP({ severity: e.target.value })} aria-label={t('common:sqq.severity')}>
                {['high', 'mid', 'low'].map(s => <option key={s} value={s}>{t(`common:sqq.sev.${s}`)}</option>)}
              </select>
              <button type="button" className="app-btn app-btn-danger" aria-label={t('common:ui.delete')} onClick={() => setPatterns(body.patterns.filter((_, j) => j !== i))}><FiX size={11} /></button>
            </div>
            {(p.mode || 'keywords') === 'keywords' ? (
              <div className="stn-grid2">
                <label className="stn-field"><span>{t('common:sqq.patternInclude')}</span>
                  <textarea className="srv-mono" rows={2} value={(p.include || []).join('\n')} placeholder={t('common:sqq.patternIncludePh')} onChange={e => setP({ include: e.target.value.split('\n') })} /></label>
                <label className="stn-field"><span>{t('common:sqq.patternExclude')}</span>
                  <textarea className="srv-mono" rows={2} value={(p.exclude || []).join('\n')} placeholder={t('common:sqq.patternExcludePh')} onChange={e => setP({ exclude: e.target.value.split('\n') })} /></label>
              </div>
            ) : (
              <div className="stn-row sqq-pattern-sample">
                <label className="stn-field stn-grow"><span>{t('common:sqq.patternSample')}</span>
                  <textarea className="srv-mono" rows={3} value={p.sample || ''} placeholder={t('common:sqq.patternSamplePh')} onChange={e => setP({ sample: e.target.value })} /></label>
                <label className="stn-field"><span>{t('common:sqq.patternThreshold')}</span>
                  <input type="number" min="0.3" max="1" step="0.05" className="sqq-num" value={p.threshold ?? 0.8} onChange={e => setP({ threshold: Number(e.target.value) })} /></label>
              </div>
            )}
            <input value={p.note || ''} placeholder={t('common:sqq.patternNotePh')} onChange={e => setP({ note: e.target.value })} />
          </div>
        );
      })}
      <div><button type="button" className="app-btn" onClick={() => setPatterns([...(body.patterns || []), { name: '', mode: 'keywords', include: [], exclude: [], severity: 'mid', threshold: 0.8, note: '', on: true }])}><FiPlus size={12} /> {t('common:sqq.addPattern')}</button></div>
    </div>
  );

  const renderBiz = () => (
    <div className="stn-box">
      <span className="stn-note">{t('common:sqq.bizHint')}</span>
      {(body.biz || []).map((b, i) => (
        // eslint-disable-next-line react/no-array-index-key -- 순서가 곧 우선순위다
        <div key={i} className="stn-row sqq-biz">
          <select value={b.field} onChange={e => setBiz(body.biz.map((x, j) => (j === i ? { ...x, field: e.target.value } : x)))}>
            {['schema', 'module', 'sqlId', 'text'].map(f => <option key={f} value={f}>{t(`common:sqq.bizField.${f}`)}</option>)}
          </select>
          <select value={b.op} onChange={e => setBiz(body.biz.map((x, j) => (j === i ? { ...x, op: e.target.value } : x)))}>
            {['eq', 'prefix', 'contains'].map(o => <option key={o} value={o}>{t(`common:sqq.bizOp.${o}`)}</option>)}
          </select>
          <input className="stn-grow mono" value={b.value} placeholder={t('common:sqq.bizValuePh')} onChange={e => setBiz(body.biz.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
          <span>→</span>
          <input value={b.label} placeholder={t('common:sqq.bizLabelPh')} onChange={e => setBiz(body.biz.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
          <button type="button" className="app-btn app-btn-danger" aria-label={t('common:ui.delete')} onClick={() => setBiz(body.biz.filter((_, j) => j !== i))}><FiX size={11} /></button>
        </div>
      ))}
      <div><button type="button" className="app-btn" onClick={() => setBiz([...(body.biz || []), { field: 'module', op: 'prefix', value: '', label: '' }])}><FiPlus size={12} /> {t('common:sqq.addBiz')}</button></div>
    </div>
  );

  const renderReview = () => {
    const on = Object.entries(body.checks || {}).filter(([, v]) => v.on);
    return (
      <>
        <CriteriaSummary body={body} t={t} />
        <div className="stn-box">
          <b>{t('common:sqq.checksOn', { n: on.length })}</b>
          <ul className="sqq-hist">{on.map(([id, v]) => <li key={id}><span className={`sqq-sev s-${v.severity}`}>{t(`common:sqq.sev.${v.severity}`)}</span> {t(`common:sqq.check.${id}`)}</li>)}</ul>
        </div>
        <div className="stn-box">
          <b>{t('common:sqq.rulesTitle')} ({(body.guideRules || []).length})</b>
          <ul className="sqq-hist">{(body.guideRules || []).map((r, i) => <li key={r.ruleId}><b>R{i + 1}</b> {r.principle}</li>)}</ul>
        </div>
        <div className="stn-box">
          <b>{t('common:sqq.group.pattern')} ({(body.patterns || []).length})</b>
          <ul className="sqq-hist">{(body.patterns || []).map((p, i) => (
            // eslint-disable-next-line react/no-array-index-key -- 새 패턴은 id 가 없다
            <li key={p.id || i}><b>{p.name}</b> <span className="stn-note">— {t(`common:sqq.patternModes.${p.mode || 'keywords'}`)}{p.note ? ` · ${p.note}` : ''}</span></li>
          ))}</ul>
        </div>
        {!readOnly && dirty && <div className="sau-notice">{t('common:sqq.unsaved')}</div>}
      </>
    );
  };

  const renderHistory = () => (
    <div className="stn-box">
      {!(sel.history || []).length && <span className="stn-note">{readOnly ? t('common:sqq.builtinNoHistory') : t('common:sqq.hist.none')}</span>}
      <ul className="sqq-hist">
        {[...(sel.history || [])].reverse().map((h, i) => (
          // eslint-disable-next-line react/no-array-index-key -- 이력은 순서가 곧 키다
          <li key={i}><span className="mono">{storedTime(h.at, 'min')}</span> {(h.changes || []).length ? h.changes.map(c => describeChange(c, t)).join(' · ') : t('common:sqq.hist.noChange')}</li>
        ))}
      </ul>
    </div>
  );

  const bodyEl = { basic: renderBasic, checks: renderChecks, rules: renderRules, patterns: renderPatterns, biz: renderBiz, review: renderReview, history: renderHistory }[step]();

  return (
    <>
      <WorkflowHead title={form.name || t('common:sqq.newCriteria')}
        badges={readOnly ? <span className="sqq-tag sqq-tag-builtin">{t('common:sqq.builtinBadge')}</span> : null}
        dirty={!readOnly && dirty}
        actions={sel.id ? <button type="button" className={`app-btn${readOnly ? ' app-btn-primary' : ''}`} onClick={() => onCopy && onCopy({ ...sel, name: form.name, body })}><FiCopy size={12} /> {t('common:sqq.copyCriteria')}</button> : null}
        onDelete={!readOnly && sel.id ? remove : null} deleteDisabled={busy}
        onSave={readOnly ? null : save} saveDisabled={busy || !form.name.trim() || !dirty} />
      <WorkflowRibbon tabs={CRIT_TABS} step={step} onStep={setStep} label={s => t(`common:sqq.critSteps.${s}`)}
        suffix={s => (s === 'history' && (sel.history || []).length ? ` (${sel.history.length})` : '')} />
      {step !== 'history' && <div className="srv-stepHelp" key={`help-${step}`}>{t(readOnly && step === 'basic' ? 'common:sqq.critHelp.builtin' : `common:sqq.critHelp.${step}`)}</div>}
      <div className="stn-step-wrap" key={`${sel.id || 'new'}-${step}`}>
        <div className="stn-step">
          {/* 기본·샘플 기준은 보기만 — 칸을 통째로 잠근다(고치려면 복사) */}
          <fieldset className="sqq-fieldset" disabled={readOnly}>{bodyEl}</fieldset>
          <div className="stn-actions">
            {idx > 0 && <button type="button" className="app-btn" onClick={() => setStep(CRIT_STEPS[idx - 1])}>{t('common:sqq.prev')}</button>}
            {idx >= 0 && idx < CRIT_STEPS.length - 1 && <button type="button" className="app-btn" onClick={() => setStep(CRIT_STEPS[idx + 1])}>{t('common:sqq.next')}</button>}
          </div>
        </div>
      </div>
    </>
  );
};

CriteriaEditor.propTypes = {
  selected: PropTypes.shape({ id: PropTypes.string, copyOf: PropTypes.object, nonce: PropTypes.number }),
  /** 저장했다(목록을 다시 읽고 그 기준을 고른 채로) */
  onSaved: PropTypes.func,
  onDeleted: PropTypes.func,
  /** 복사해서 새 기준 만들기 — 창이 { id: null, copyOf } 로 다시 고른다 */
  onCopy: PropTypes.func,
  /** 가이드 관리 창(SQL 표준) — AI 빌드에만 */
  onOpenGuides: PropTypes.func,
};

export default CriteriaEditor;
